import React, {Component} from 'react';
import ReactToPrint from 'react-to-print';
import DateRangeFilter from '../components/DateRangeFilter';
import LocationFilter from '../components/LocationFilter';
import LocationLogReport from '../components/LocationLogReport';
import RegisterLogReport from '../components/RegisterLogReport';
import ReportHeaderState from '../components/ReportHeaderState';
import NoAccess from '../noAccess';
import {getTimeZoneOffsetDefault} from '../shared/dateTimeLists';
import {hasReportsAccess} from '../shared/authFunctions';
import FuseTheme from '../global/theme';
import {ViewTitle} from 'admin-on-rest/lib/mui';
import {Card, CardText} from 'material-ui/Card';
import CircularProgress from 'material-ui/CircularProgress';
import RaisedButton from 'material-ui/RaisedButton';
import Avatar from 'material-ui/Avatar';
import Icon from 'material-ui/svg-icons/action/description';

export const fuseLocationRegisterLogReportsIcon = Icon;

const styles = {
    button: {color: FuseTheme.palette.primary3Color},
};

/**
 * Location Register Log Reports class for retrieving and displaying summary
 *
 */
export class LocationRegisterLogReports extends Component {
    constructor(props) {
        super(props);
        const system = localStorage.getItem('system');
        const offsetDefault = getTimeZoneOffsetDefault();
        this.state = {
            errorMessageRegister: null,
            errorMessageLocation: null,
            location: {
                id: null,
                name: '',
                city: '',
                state: ''
            },
            locationSummaryComplete: false,
            registerSummaryComplete: false,
            selectedEndDate: null,
            selectedStartDate: null,
            selectedOffset: offsetDefault,
            summariesComplete: false,
            system: system
        };
    }

    /**
     * Convert Dates to visual display
     *
     * @param selectedStartDate
     * @param selectedEndDate
     * @return {string}
     */
    displaySelectedDates(selectedStartDate, selectedEndDate) {
        const options = {year: 'numeric', month: 'long', day: 'numeric'};
        let startDate = new Date(selectedStartDate + 'T00:00:00');
        let endDate = new Date(selectedEndDate + 'T00:00:00');
        let startDateStr = startDate.toLocaleDateString('en-US', options);
        let endDateStr = endDate.toLocaleDateString('en-US', options);

        return (
            'Date Range: ' + startDateStr + ' through ' + endDateStr
        )
    }

    /**
     * Process when all table components have completed processing
     */
    checkSummariesComplete = () => {
        if (this.state.locationSummaryComplete && this.state.registerSummaryComplete) {
            this.setState({
                summariesComplete: true
            });
        }
    };

    /**
     * Handle event props from DateRangeFilter
     *
     * @param type
     * @param value
     */
    handleFilterSelect = (type, value) => {
        switch (type) {
            case 'location':
                this.setState({
                    selectedLocation: value,
                    summariesComplete: false
                });
                break;
            case 'start_date':
                this.setState({
                    selectedStartDate: value,
                    summariesComplete: false
                });
                break;
            case 'end_date':
                this.setState({
                    selectedEndDate: value,
                    summariesComplete: false
                });
                break;
            case 'offset':
                this.setState({
                    selectedOffset: value,
                    summariesComplete: false
                });
                break;
            default:
                return
        }
    };

    /**
     * Handle event props for Location Log Summary table completion
     *
     * @param type
     * @param isComplete
     */
    handleSummaryComplete = (type, isComplete) => {
        if (type === 'location') {
            this.setState({
                locationSummaryComplete: isComplete,
            }, () => {
                this.checkSummariesComplete();
            });
        }
        if (type === 'register') {
            this.setState({
                registerSummaryComplete: isComplete,
            }, () => {
                this.checkSummariesComplete();
            });
        }
    };

    handleReportError = (type, message) => {
        if (type === 'location') {
            this.setState({
                errorMessageLocation: message,
                locationSummaryComplete: true,
            }, () => {
                this.checkSummariesComplete();
            });
        }
        if (type === 'register') {
            this.setState({
                errorMessageRegister: message,
                registerSummaryComplete: true
            }, () => {
                this.checkSummariesComplete();
            });
        }
    };

    render() {
        const {
            errorMessageLocation,
            errorMessageRegister,
            selectedLocation,
            selectedEndDate,
            selectedStartDate,
            selectedOffset,
            summariesComplete,
            system
        } = this.state;

        if (!hasReportsAccess()) return <NoAccess/>;

        const selectedLocationId = (selectedLocation && selectedLocation.id) ? selectedLocation.id.toString() : '';

        const ListTitle = <span className="list-title hide-print">
            <Avatar icon={<Icon/>} className="avatar-title"/>Location Register Log Reports</span>;

        const ListDescription = () => {
            return (
                <span className="list-description hide-print">
                    <p>Location Log and Register Log Reports display all open and close operations for the selected
                        Location and all registers for the Location, in a selected date range. Each log item can
                        display additional data for review.</p>
                    <p>The range of dates may be no longer than a year apart.</p>
                    <p>Select a Location, Date Range, and Time Zone Offset to generate Reports:</p>
                </span>
            );
        };

        return (
            <div className="lists-container" ref={e => (this.printReportRef = e)}>
                <Card>
                    <ViewTitle title={ListTitle}/>
                    <CardText>
                        <ListDescription/>

                        {/* Report Filters (Location, Start Date, End Date, Time Zone) */}
                        <LocationFilter
                            label="Select a Location:"
                            onSelectLocation={this.handleFilterSelect}
                            selectionType="object"
                        />
                        <DateRangeFilter
                            limitEndRange="1-year-after"
                            onSelectStartDate={this.handleFilterSelect}
                            onSelectEndDate={this.handleFilterSelect}
                            onSelectOffset={this.handleFilterSelect}
                        />

                        {/* Print Header */}
                        {selectedStartDate && selectedEndDate &&
                        <ReportHeaderState
                            reportType="Location Register Log Report Summary"
                        />
                        }

                        {/* Location Register/Log Report Content */}
                        {selectedStartDate && selectedEndDate && selectedOffset &&
                        <div>

                            {/* Report Header, Loader, and messaging */}
                            <div className="report-header">
                                <div className="btn-print-container report-header-button-container">
                                    <ReactToPrint
                                        trigger={() =>
                                            <RaisedButton
                                                primary
                                                type="button"
                                                label="Print Report"
                                                style={styles.button}
                                            />
                                        }
                                        content={() => this.printReportRef}
                                    />
                                </div>
                                <h2 className="report-header-title">Location/Register Log Reports</h2>
                                <p>
                                    {this.displaySelectedDates(selectedStartDate, selectedEndDate)}
                                </p>
                            </div>
                            {!summariesComplete &&
                            <div className="loader-container-left">
                                <h4>Generating Reports...</h4>
                                <CircularProgress
                                    size={24}
                                    thickness={2}
                                    className="loader"
                                />
                            </div>
                            }

                            {/* Location Log Report */}
                            <LocationLogReport
                                locationId={selectedLocationId}
                                startDate={selectedStartDate}
                                endDate={selectedEndDate}
                                offset={selectedOffset}
                                onSummaryComplete={this.handleSummaryComplete}
                                onError={this.handleReportError}
                                system={system}
                            />
                            {/* Error and Messaging */}
                            {errorMessageLocation &&
                                <div className="error">
                                    <p>{errorMessageLocation}</p>
                                </div>
                            }

                            {/* Register Log Report */}
                            <RegisterLogReport
                                locationId={selectedLocationId}
                                startDate={selectedStartDate}
                                endDate={selectedEndDate}
                                offset={selectedOffset}
                                onSummaryComplete={this.handleSummaryComplete}
                                onError={this.handleReportError}
                                system={system}
                            />
                            {/* Error and Messaging */}
                            {errorMessageRegister &&
                                <div className="error">
                                    <p>{errorMessageRegister}</p>
                                </div>
                            }
                        </div>
                        }
                    </CardText>
                </Card>
            </div>
        )
    }
}



// WEBPACK FOOTER //
// ./src/Fuse/Report/LocationRegisterLogReports.js